import type {
  FilterOption,
  MultiselectFilterSet,
  QueryParams,
} from '@/components/partials/jobs/JobsBackOffice/JobsBackOfficeFilters/types';
import {
  filterKeyToHuman,
  selectedOptionsToQueryParams,
  setSelectedMapper,
} from '@/components/partials/jobs/JobsBackOffice/JobsBackOfficeFilters/utils';
import { JobsVacancyEmployment, JobsRole, JobsVacancyState, JobsModeratorsEmployerPlan } from '@/types/graphql';
import type { FilterKeysVacancies } from './types';
import { EMPLOYMENT_TYPES } from '@/components/partials/jobs/constants';

export const PLANS_FILTER_OPTIONS: FilterOption[] = [
  {
    title: 'Full',
    key: JobsModeratorsEmployerPlan.Full,
  },
  {
    title: 'Demo',
    key: JobsModeratorsEmployerPlan.Demo,
  },
];

export const TIERS_ACCEPTED_FILTER_OPTIONS: FilterOption[] = [
  {
    title: '0',
    key: 0,
  },
  {
    title: '1',
    key: 1,
  },
  {
    title: '2',
    key: 2,
  },
  {
    title: '3',
    key: 3,
  },
  {
    title: '4',
    key: 4,
  },
];

export const SASS_FILTER_OPTIONS: FilterOption[] = [
  {
    title: 'Yes',
    key: true,
  },
  {
    title: 'No',
    key: false,
  },
];

const POSITION_FILTER_OPTIONS: FilterOption[] = [
  {
    title: 'AE',
    key: JobsRole.AccountExecutive,
  },
  {
    title: 'SDR',
    key: JobsRole.Sdr,
  },
  {
    title: 'CSM/AM',
    key: JobsRole.CsmAm,
  },
  {
    title: 'VP/Manager',
    key: JobsRole.Vp,
  },
  {
    title: 'Other',
    key: JobsRole.Other,
  },
];

export const EMPLOYMENT_TYPE_FILTER_OPTIONS: FilterOption[] = [
  {
    title: EMPLOYMENT_TYPES[JobsVacancyEmployment.FullTime],
    key: JobsVacancyEmployment.FullTime,
  },
  {
    title: EMPLOYMENT_TYPES[JobsVacancyEmployment.Contract],
    key: JobsVacancyEmployment.Contract,
  },
  {
    title: EMPLOYMENT_TYPES[JobsVacancyEmployment.SideHustle],
    key: JobsVacancyEmployment.SideHustle,
  },
];

export const VACANCY_STATE_FILTER_OPTIONS: FilterOption[] = [
  {
    title: filterKeyToHuman(JobsVacancyState.Activated),
    key: JobsVacancyState.Activated,
  },
  {
    title: filterKeyToHuman(JobsVacancyState.Deactivated),
    key: JobsVacancyState.Deactivated,
  },
  {
    title: filterKeyToHuman(JobsVacancyState.Disabled),
    key: JobsVacancyState.Disabled,
  },
  {
    title: filterKeyToHuman(JobsVacancyState.Draft),
    key: JobsVacancyState.Draft,
  },
  {
    title: filterKeyToHuman(JobsVacancyState.DeletedFromAts),
    key: JobsVacancyState.DeletedFromAts,
  },
  {
    title: filterKeyToHuman(JobsVacancyState.Deleted),
    key: JobsVacancyState.Deleted,
  },
];

export const filterOptions: {
  [K in Extract<
    FilterKeysVacancies,
    'states' | 'employment_types' | 'positions' | 'employer_plans' | 'accepted_tiers' | 'is_working_in_sales_saas'
  >]: FilterOption[];
} = {
  positions: POSITION_FILTER_OPTIONS.map(setSelectedMapper()),
  employment_types: EMPLOYMENT_TYPE_FILTER_OPTIONS.map(setSelectedMapper()),
  states: VACANCY_STATE_FILTER_OPTIONS.map(setSelectedMapper()),
  employer_plans: PLANS_FILTER_OPTIONS.map(setSelectedMapper()),
  accepted_tiers: TIERS_ACCEPTED_FILTER_OPTIONS.map(setSelectedMapper()),
  is_working_in_sales_saas: SASS_FILTER_OPTIONS.map(setSelectedMapper()),
};

const DEFAULT_SELECTED_STATES = [JobsVacancyState.Activated];
const DEFAULT_EMPLOYER_PLANS = [PLANS_FILTER_OPTIONS[0].key as string];

export const VACANCIES_PAGE_DEFAULT_FILTERS: MultiselectFilterSet = Object.freeze({
  states: DEFAULT_SELECTED_STATES,
  employer_plans: DEFAULT_EMPLOYER_PLANS,
});

export const VACANCIES_PAGE_DEFAULT_FILTERS_QUERY_PARAMS: QueryParams =
  selectedOptionsToQueryParams(VACANCIES_PAGE_DEFAULT_FILTERS);
